import React, { useEffect, useState } from 'react';
import PasswordInput from './password-input';
import styles from '../style/registration-page.module.css';
import { useHistory } from 'react-router-dom';
import { notification, Select } from 'antd';
import axios from 'axios';
import assessments from '../config/assessments';

const professionOptions = [
  'School Psychologist',
  'Speech-Language Pathologist',
  'Special Education Teacher',
  'Educational Diagnostician',
  'Clinical Psychologist',
  'Neuropsychologist',
  'Occupational Therapist',
  'Counselor',
  'Other'
];

const stateOptions = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
  'DC'
];

const RegistrationPage = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [profession, setProfession] = useState(undefined);
  const [organization, setOrganization] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState(undefined);
  const [zip, setZip] = useState('');
  const [country, setCountry] = useState('');
  const [product, setProduct] = useState([]);
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const history = useHistory();

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'auto';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const markTouched = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const getErrors = () => {
    const errs = {};
    if (!firstName.trim()) errs.firstName = 'First name is required.';
    if (!lastName.trim()) errs.lastName = 'Last name is required.';
    if (!email.trim()) {
      errs.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      errs.email = 'Please enter a valid email address.';
    }
    if (!password) {
      errs.password = 'Password is required.';
    } else if (password.length < 8) {
      errs.password = 'Password must be at least 8 characters.';
    }
    if (phone.trim() && !/^\+?[\d\s\-().]{7,15}$/.test(phone.trim())) {
      errs.phone = 'Please enter a valid phone number.';
    }
    if (zip.trim() && !/^\d{5}(-\d{4})?$/.test(zip.trim())) {
      errs.zip = 'Please enter a valid zip code.';
    }
    return errs;
  };

  const errors = getErrors();
  const isFormValid = Object.keys(errors).length === 0;

  const handleSubmit = async () => {
    if (!isFormValid || submitting) return;
    setSubmitting(true);

    const data = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      phone: phone.trim(),
      profession,
      kind: 'Examiner',
      organization: organization.trim(),
      city: city.trim(),
      state,
      zip: zip.trim(),
      country: country.trim(),
      product,
      password
    };

    const config = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    return axios
      .post(process.env.REACT_APP_API_URL + '/user/register', data, config)
      .then(() => {
        notification.success({
          description: 'Your registration has been submitted. Approval may take up to 48 hours.'
        });
        history.push('/');
      })
      .catch((error) => {
        console.log(error);
        setSubmitting(false);
      });
  };

  return (
    <div className={styles['container-a']}>
      <label className={styles['register-title']}>
        Schoolhouse Educational Services Assessments
      </label>
      <div className={styles['register-container']}>
        <div className={styles['register-card']}>
          <div className={styles['form-grid']}>
            <div className={styles['form-field']}>
              <label className={styles['field-label']}>First Name *</label>
              <input
                aria-invalid={touched.firstName && !!errors.firstName}
                className={`${styles['input']}${touched.firstName && errors.firstName ? ' ' + styles['input-error'] : ''}`}
                onBlur={() => markTouched('firstName')}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First Name"
                type="text"
                value={firstName}
              />
              {touched.firstName && errors.firstName && (
                <span className={styles['error-message']}>{errors.firstName}</span>
              )}
            </div>
            <div className={styles['form-field']}>
              <label className={styles['field-label']}>Last Name *</label>
              <input
                aria-invalid={touched.lastName && !!errors.lastName}
                className={`${styles['input']}${touched.lastName && errors.lastName ? ' ' + styles['input-error'] : ''}`}
                onBlur={() => markTouched('lastName')}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last Name"
                type="text"
                value={lastName}
              />
              {touched.lastName && errors.lastName && (
                <span className={styles['error-message']}>{errors.lastName}</span>
              )}
            </div>
            <div className={styles['form-field']}>
              <label className={styles['field-label']}>Email *</label>
              <input
                aria-invalid={touched.email && !!errors.email}
                className={`${styles['input']}${touched.email && errors.email ? ' ' + styles['input-error'] : ''}`}
                onBlur={() => markTouched('email')}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                type="email"
                value={email}
              />
              {touched.email && errors.email && (
                <span className={styles['error-message']}>{errors.email}</span>
              )}
            </div>
            <div className={styles['form-field']}>
              <label className={styles['field-label']}>Phone Number</label>
              <input
                aria-invalid={touched.phone && !!errors.phone}
                className={`${styles['input']}${touched.phone && errors.phone ? ' ' + styles['input-error'] : ''}`}
                onBlur={() => markTouched('phone')}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone Number"
                type="tel"
                value={phone}
              />
              {touched.phone && errors.phone && (
                <span className={styles['error-message']}>{errors.phone}</span>
              )}
            </div>
            <div className={styles['form-field']}>
              <label className={styles['field-label']}>Your Profession</label>
              <Select
                onChange={(value) => setProfession(value)}
                placeholder="Select profession"
                value={profession}
              >
                {professionOptions.map((opt) => (
                  <Select.Option key={opt} value={opt}>
                    {opt}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div className={styles['form-field']}>
              <label className={styles['field-label']}>
                Name of School, Agency, or Organization
              </label>
              <input
                className={styles['input']}
                onChange={(e) => setOrganization(e.target.value)}
                placeholder="Organization"
                type="text"
                value={organization}
              />
            </div>
            <div className={styles['form-field']}>
              <label className={styles['field-label']}>City</label>
              <input
                className={styles['input']}
                onChange={(e) => setCity(e.target.value)}
                placeholder="City"
                type="text"
                value={city}
              />
            </div>
            <div className={styles['form-field']}>
              <label className={styles['field-label']}>State</label>
              <Select
                onChange={(value) => setState(value)}
                placeholder="Select state"
                showSearch
                value={state}
              >
                {stateOptions.map((s) => (
                  <Select.Option key={s} value={s}>
                    {s}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div className={styles['form-field']}>
              <label className={styles['field-label']}>Zip Code</label>
              <input
                aria-invalid={touched.zip && !!errors.zip}
                className={`${styles['input']}${touched.zip && errors.zip ? ' ' + styles['input-error'] : ''}`}
                onBlur={() => markTouched('zip')}
                onChange={(e) => setZip(e.target.value)}
                placeholder="Zip Code"
                type="text"
                value={zip}
              />
              {touched.zip && errors.zip && (
                <span className={styles['error-message']}>{errors.zip}</span>
              )}
            </div>
            <div className={styles['form-field']}>
              <label className={styles['field-label']}>Country if not USA</label>
              <input
                className={styles['input']}
                onChange={(e) => setCountry(e.target.value)}
                placeholder="Country"
                type="text"
                value={country}
              />
            </div>
            <div className={styles['form-field']}>
              <label className={styles['field-label']}>
                Schoolhouse Assessment product(s) you plan to use
              </label>
              <Select
                mode="multiple"
                onChange={(value) => setProduct(value)}
                placeholder="Select product(s)"
                value={product}
              >
                {assessments.map((p) => (
                  <Select.Option key={p} value={p}>
                    {p}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div className={styles['form-field']}>
              <label className={styles['field-label']}>Create your password *</label>
              <PasswordInput
                errorClassName={styles['input-error']}
                hasError={touched.password && !!errors.password}
                inputClassName={styles['input']}
                onBlur={() => markTouched('password')}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                value={password}
              />
              {touched.password && errors.password && (
                <span className={styles['error-message']}>{errors.password}</span>
              )}
            </div>
          </div>
          <div className={styles['button-container']}>
            <button
              className={`${styles['button']}${!isFormValid || submitting ? ' ' + styles['button-disabled'] : ''}`}
              disabled={!isFormValid || submitting}
              onClick={handleSubmit}
            >
              {submitting ? 'Registering...' : 'Register'}
            </button>
          </div>
          <div className={styles['action-container']}>
            <div onClick={() => history.push('/')}>
              <label className={styles['action']}>Back to Sign In</label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationPage;
